import type {
  CreatorSegmentPreviewMatchedValue,
  CreatorSegmentPreviewMember,
  CreatorSegmentPreviewPlatformLink,
  CreatorSegmentPreviewReference,
  CriteriaGroupDto,
  MoneyDto,
  Preview200,
  PreviewParams,
  SegmentCriteriaDto,
} from '../generated/social-media/metapicSocialMediaAPIDocumentation.schemas'
import type { CamelCased } from '../types/requests.types'
import {
  type SegmentCreator,
  SegmentCreatorSourceEnum,
  type SegmentCreatorValue,
  SegmentCreatorValueKindEnum,
  type SegmentPreviewFilter,
  type SegmentPreviewResult,
} from '../types/segment-preview.types'

type SegmentPreviewMemberServer = CamelCased<CreatorSegmentPreviewMember>
type SegmentPreviewMatchedValueServer = CamelCased<CreatorSegmentPreviewMatchedValue>['value']
type SegmentPreviewResponseServer = CamelCased<Preview200>
type CriteriaFilterNode = CriteriaGroupDto['children'][number]

const isPlatformLink = (
  value: SegmentPreviewMatchedValueServer,
): value is CamelCased<CreatorSegmentPreviewPlatformLink> =>
  typeof value === 'object' && value !== null && 'handle' in value

const isReference = (
  value: SegmentPreviewMatchedValueServer,
): value is CreatorSegmentPreviewReference =>
  typeof value === 'object' && value !== null && 'id' in value

const isMoney = (value: SegmentPreviewMatchedValueServer): value is MoneyDto =>
  typeof value === 'object' && value !== null && 'amount' in value

/**
 * The response carries bare values keyed by criteria node id, so the node the
 * caller sent decides how each one is read.
 */
const adaptSegmentCreatorValue = (
  filter: CriteriaFilterNode,
  value: SegmentPreviewMatchedValueServer,
): SegmentCreatorValue | null => {
  // The generated filter DTOs share one `kind` union, so the node is told apart
  // by the field only it carries.
  if ('platform' in filter) {
    return isPlatformLink(value)
      ? {
          kind: SegmentCreatorValueKindEnum.Platform,
          platform: filter.platform,
          handle: value.handle,
          followers: value.numberOfFollowers,
          isConnected: value.state === 'connected',
        }
      : null
  }

  if ('tiers' in filter) {
    return isReference(value) ? { kind: SegmentCreatorValueKindEnum.RevenueTier, ...value } : null
  }

  if ('clients' in filter) {
    return isReference(value) ? { kind: SegmentCreatorValueKindEnum.Client, ...value } : null
  }

  if ('field' in filter) {
    return typeof value === 'string'
      ? { kind: SegmentCreatorValueKindEnum.User, field: filter.field, date: value }
      : null
  }

  if ('metric' in filter) {
    if (typeof value === 'number') {
      return { kind: SegmentCreatorValueKindEnum.Analytics, metric: filter.metric, value }
    }

    return isMoney(value)
      ? {
          kind: SegmentCreatorValueKindEnum.Analytics,
          metric: filter.metric,
          value: { amount: Number(value.amount), currency: value.currency },
        }
      : null
  }

  return null
}

const isRepeatedAccount = (
  value: SegmentCreatorValue,
  index: number,
  values: SegmentCreatorValue[],
): boolean =>
  value.kind === SegmentCreatorValueKindEnum.Platform &&
  value.handle !== null &&
  values
    .slice(0, index)
    .some(
      (earlier) =>
        earlier.kind === SegmentCreatorValueKindEnum.Platform &&
        earlier.platform === value.platform &&
        earlier.handle === value.handle,
    )

const adaptSegmentCreator = (
  filters: CriteriaFilterNode[],
  member: SegmentPreviewMemberServer,
): SegmentCreator => ({
  id: member.userId,
  username: member.username,
  image: member.image,
  source:
    member.source === 'manual' ? SegmentCreatorSourceEnum.Manual : SegmentCreatorSourceEnum.Rule,
  matchedValues: filters
    .map((filter) =>
      adaptSegmentCreatorValue(
        filter,
        member.matchedValues.find(({ nodeId }) => nodeId === filter.id)?.value ?? null,
      ),
    )
    .filter((value) => value !== null)
    .filter((value, index, values) => !isRepeatedAccount(value, index, values)),
})

export const adaptSegmentPreview = (
  response: SegmentPreviewResponseServer,
  criteria: SegmentCriteriaDto | null,
): SegmentPreviewResult => {
  const filters = criteria?.root.children ?? []

  return {
    creators: response.items.map((member) => adaptSegmentCreator(filters, member)),
    totalCount: response.meta.totalItems,
    totalMembers: response.totalMembers,
    totalPages: response.meta.totalPages,
    exceedsMemberCap: response.exceedsMemberCap,
  }
}

export const handleSegmentPreviewParams = (filter: SegmentPreviewFilter): PreviewParams => ({
  page: filter.page,
  limit: filter.size,
  ...(filter.query ? { search: filter.query } : {}),
})
